import { useCallback, useState } from 'react'
import { Icon } from '@iconify/react'
import { Dropdown } from 'antd'

import { getGapSplitStyle, getJourneySpan, getStoryMapFilterClass, isUntitledName, parseJourneyLabel } from '../utils'
import useCloseDropdownOnScroll from '../hooks/useCloseDropdownOnScroll'
import InsertZone from './InsertZone'
import styles from '../../ProjectStoryMapBoard.module.css'

const JourneyCard = ({
    journey,
    journeyIndex,
    journeyMenu,
    onOpen,
    isFilterActive = false,
    isFilterMatchCurrent = false,
}) => {
    const journeyLabel = parseJourneyLabel(journey.title)
    const [menuOpen, setMenuOpen] = useState(false)
    const closeMenu = useCallback(() => setMenuOpen(false), [])
    useCloseDropdownOnScroll(menuOpen, closeMenu)
    const filterClass = getStoryMapFilterClass(
        isFilterActive,
        journey.matchesFilter,
        styles
    )
    const isFilterMatch =
        isFilterActive && journey.matchesFilter === true

    const handleOpen = (event) => {
        // Draft journeys exist only client-side — the detail drawer would 404.
        if (journey.isDraft) return
        if (
            event.target.closest('button') ||
            event.target.closest('.ant-dropdown') ||
            event.target.closest('.ant-dropdown-menu') ||
            event.target.closest('.ant-dropdown-menu-item')
        ) {
            return
        }
        onOpen?.(journey.id)
    }

    return (
    <div
        className={`${styles.journeyCard} ${
            journey.isDraft ? styles.journeyCardAgentDraft : ''
        } ${filterClass} ${
            isFilterMatchCurrent ? styles.filterMatchCurrent : ''
        }`}
        data-story-map-journey-id={journey.id}
        {...(isFilterMatch ? { 'data-story-map-filter-match': 'true' } : {})}
        onClick={handleOpen}
        onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                if (!journey.isDraft) onOpen?.(journey.id)
            }
        }}
        role="button"
        tabIndex={0}
    >
        <div className={styles.journeyHeader}>
            <span className={styles.journeyAccent} aria-hidden="true" />
            <span
                className={`${styles.journeyTitle} ${
                    isUntitledName(journeyLabel) ? styles.untitledLabel : ''
                }`}
                title={`${journeyIndex + 1}. ${journeyLabel}`}
            >
                {journeyIndex + 1}. {journeyLabel}
            </span>
            {journey.isDraft ? (
                <span className={styles.agentDraftBadge}>AI</span>
            ) : null}
            {!journey.isDraft && (journeyMenu?.(journey.id) ?? []).length > 0 ? (
                <Dropdown
                    menu={{ items: journeyMenu(journey.id) }}
                    trigger={['click']}
                    placement="bottomRight"
                    open={menuOpen}
                    onOpenChange={setMenuOpen}
                    getPopupContainer={() => document.body}
                    destroyPopupOnHide
                >
                    <button
                        type="button"
                        className={styles.menuBtn}
                        aria-label="Journey options"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <Icon icon="mdi:dots-vertical" width={16} />
                    </button>
                </Dropdown>
            ) : null}
        </div>
    </div>
    )
}

const JourneyEditCard = ({
    journey,
    journeyIndex,
    journeyInputRef,
    editingJourneyDraft,
    onDraftChange,
    onCommit,
}) => (
    <div className={styles.journeyEditCard}>
        <div className={styles.journeyHeader}>
            <span className={styles.journeyAccent} aria-hidden="true" />
            <div className={styles.journeyTitleField}>
                <span className={styles.journeyEditPrefix}>{journeyIndex + 1}. </span>
                <input
                    ref={journeyInputRef}
                    type="text"
                    className={styles.journeyTitleInput}
                    value={editingJourneyDraft}
                    placeholder="Untitled journey"
                    aria-label="Journey name"
                    onChange={(event) => onDraftChange(event.target.value)}
                    onBlur={(event) => onCommit(journey.id, event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault()
                            onCommit(journey.id, event.currentTarget.value)
                        }
                    }}
                />
            </div>
        </div>
    </div>
)

const JourneyRow = ({
    journeys,
    boardTracks,
    editingJourneyId,
    editingJourneyDraft,
    journeyInputRef,
    journeyMenu,
    onDraftChange,
    onCommitJourneyTitle,
    onInsertJourneyAt,
    onOpenJourney,
    isFilterActive = false,
    filterFocusedCardId = null,
    readOnly = false,
}) => {
    let gridColumn = 1
    const elements = []

    journeys.forEach((journey, journeyIndex) => {
        const span = getJourneySpan(journey.steps.length)
        const isEditingJourney = editingJourneyId === journey.id
        const journeyGroupFilterClass =
            isFilterActive && journey.matchesFilter === true
                ? styles.journeyGroupFilterMatch
                : ''

        elements.push(
            <div
                key={journey.id}
                className={`${styles.journeyGroup} ${journeyGroupFilterClass}`}
                style={{
                    gridColumn: `${gridColumn} / span ${span}`,
                    gridRow: 1,
                }}
            >
                {isEditingJourney ? (
                    <JourneyEditCard
                        journey={journey}
                        journeyIndex={journeyIndex}
                        journeyInputRef={journeyInputRef}
                        editingJourneyDraft={editingJourneyDraft}
                        onDraftChange={onDraftChange}
                        onCommit={onCommitJourneyTitle}
                    />
                ) : (
                    <JourneyCard
                        journey={journey}
                        journeyIndex={journeyIndex}
                        journeyMenu={journeyMenu}
                        onOpen={onOpenJourney}
                        isFilterActive={isFilterActive}
                        isFilterMatchCurrent={
                            String(filterFocusedCardId) === String(journey.id)
                        }
                    />
                )}
            </div>
        )

        gridColumn += span

        if (journeyIndex < journeys.length - 1) {
            const journeyGapTrackIndex = boardTracks.findIndex(
                (track) =>
                    track.type === 'gap' &&
                    track.gapKind === 'journey' &&
                    track.journeyIndex === journeyIndex
            )

            if (!readOnly && !isFilterActive) {
                elements.push(
                    <InsertZone
                        key={`journey-insert-${journeyIndex}`}
                        variant="journey"
                        label="Add journey"
                        style={{ gridColumn, gridRow: 1 }}
                        gapSplitStyle={
                            journeyGapTrackIndex >= 0
                                ? getGapSplitStyle(
                                      boardTracks,
                                      journeyGapTrackIndex,
                                      styles
                                  )
                                : undefined
                        }
                        onClick={() => onInsertJourneyAt(journeyIndex + 1)}
                    />
                )
            }
            gridColumn += 1
        }
    })

    if (journeys.length > 0 && !readOnly && !isFilterActive) {
        const trailingTrackIndex = boardTracks.findIndex(
            (track) => track.type === 'gap' && track.gapKind === 'trailing'
        )

        elements.push(
            <InsertZone
                key="journey-insert-trailing"
                variant="journey"
                label="Add journey"
                style={{ gridColumn, gridRow: 1 }}
                gapSplitStyle={
                    trailingTrackIndex >= 0
                        ? getGapSplitStyle(boardTracks, trailingTrackIndex, styles)
                        : undefined
                }
                onClick={() => onInsertJourneyAt(journeys.length)}
            />
        )
    }

    return elements
}

export default JourneyRow
